import { HOME_CURRENCY, MONTH_NAMES, periodLabel, periodParts } from './format.js';

/**
 * Receipt-level validation, ported from the prototype.
 *
 * The date rules matter more than they look: Philippine receipts mix DD/MM and
 * MM/DD freely, so a receipt printed 03/08/2026 is genuinely ambiguous. The
 * prototype resolved this by asking the requestor which reading is correct, but
 * only when swapping day and month would actually land inside the claim period —
 * otherwise there is nothing to ask about and the receipt is simply rejected.
 */

export interface ExtractedReceipt {
  date: string;
  raw_date_text?: string;
  date_ambiguous?: boolean;
  receipt_number?: string;
  company_name?: string;
  tin_number?: string;
  description?: string;
  amount?: number;
  vat_status?: string;
}

export type DateCheck =
  | { status: 'ok'; date: string }
  | { status: 'reject'; reason: string }
  | { status: 'ask'; original: string; swapped: string; raw: string };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function dateParts(dateStr: string | undefined): { y: number; m: number; d: number } | null {
  if (!dateStr || !ISO_DATE.test(dateStr)) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (y == null || m == null || d == null) return null;
  return { y, m, d };
}

/**
 * Swap the day and month components. Returns null when the swap is impossible
 * (either component above 12 means the order is already unambiguous).
 */
export function swapDayMonth(dateStr: string): string | null {
  const dp = dateParts(dateStr);
  if (!dp || dp.m > 12 || dp.d > 12) return null;
  return `${dp.y}-${String(dp.d).padStart(2, '0')}-${String(dp.m).padStart(2, '0')}`;
}

/** Check a receipt's date against the claim's covered period. */
export function validateReceiptDate(rec: ExtractedReceipt, period: string): DateCheck {
  const dp = dateParts(rec.date);
  if (!dp) {
    return { status: 'reject', reason: 'Could not read a valid date from this receipt.' };
  }

  const { y: cy, m: cm } = periodParts(period);

  if (dp.y !== cy) {
    return {
      status: 'reject',
      reason: `This receipt is dated ${rec.date} (year ${dp.y}), but this claim covers ${periodLabel(period)}. Receipts from a different year can't be included in this claim.`,
    };
  }

  if (dp.m === cm) return { status: 'ok', date: rec.date };

  if (rec.date_ambiguous) {
    const swapped = swapDayMonth(rec.date);
    if (swapped) {
      const dp2 = dateParts(swapped);
      if (dp2 && dp2.y === cy && dp2.m === cm) {
        return {
          status: 'ask',
          original: rec.date,
          swapped,
          raw: rec.raw_date_text || rec.date,
        };
      }
    }
  }

  return {
    status: 'reject',
    reason: `This receipt is dated ${rec.date} (${MONTH_NAMES[dp.m]} ${dp.y}), which doesn't match ${periodLabel(period)}.`,
  };
}

/** Normalised receipt number used for duplicate detection. */
export function normalizeReceiptNo(receiptNo: string | null | undefined): string {
  return String(receiptNo ?? '').trim().toLowerCase();
}

export interface DuplicateHit {
  where: string;
}

/**
 * Find a duplicate receipt number among rows already in the working draft.
 * Duplicates across *other people's* claims are checked server-side against the
 * database, because the browser must never hold the full claim corpus.
 */
export function findDuplicateInDraft(
  rows: Array<{ id: string; receipt: string }>,
  receiptNo: string,
  excludeRowId?: string,
): DuplicateHit | null {
  const norm = normalizeReceiptNo(receiptNo);
  if (!norm) return null;
  const hit = rows.find((r) => r.id !== excludeRowId && normalizeReceiptNo(r.receipt) === norm);
  return hit ? { where: 'this same claim (already added as another row)' } : null;
}

/** Cap per receipt image, matching the prototype's 2MB limit. */
export const MAX_RECEIPT_BYTES = 2 * 1024 * 1024;

export const ALLOWED_RECEIPT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
] as const;

export function isAllowedReceiptType(contentType: string): boolean {
  return (ALLOWED_RECEIPT_TYPES as readonly string[]).includes(contentType);
}

/**
 * What the extraction model will actually accept.
 *
 * This is deliberately narrower than ALLOWED_RECEIPT_TYPES. HEIC is the format
 * an iPhone writes by default, so it must stay uploadable — the receipt is the
 * evidence and losing it is worse than not reading it — but the model's API
 * rejects `image/heic` outright with a 400. Sending one anyway produced
 * "OCR service returned 400" on the row, which tells the requester nothing and
 * looks random to them, because it depends on a camera setting they have never
 * heard of.
 *
 * So the two lists are separate: one governs what may be stored, this one
 * governs what may be sent to be read.
 */
export const OCR_READABLE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
] as const;

export function isOcrReadableType(contentType: string): boolean {
  return (OCR_READABLE_TYPES as readonly string[]).includes(contentType);
}

/**
 * True for a format the browser should re-encode to JPEG before upload, even
 * when it is already small enough to send as-is.
 */
export function needsTranscodeForOcr(contentType: string): boolean {
  return isAllowedReceiptType(contentType) && !isOcrReadableType(contentType);
}

/**
 * How far back a claim may reach: this calendar month and the two before it.
 *
 * Finance's original rule was "receipts must be dated within the last 2
 * months". It was widened to 3 on 29 September 2026. It is read as calendar
 * months rather than a rolling 90 days: filing in September covers July,
 * August and September, on the 1st and on the 30th alike. A rolling window
 * would mean the same receipt is claimable on Monday and refused on Tuesday,
 * which is impossible to explain at a counter.
 *
 * The period is the only thing that needs checking: every receipt date is
 * already required to fall inside the claim's own period, so bounding the
 * period bounds the receipts.
 */
export const CLAIMABLE_MONTHS = 3;

/** How many whole calendar months back `period` sits from `now`. */
function monthsBehind(period: string, now: Date): number {
  const { y, m } = periodParts(period);
  return (now.getFullYear() - y) * 12 + (now.getMonth() - (m - 1));
}

/** Shift a YYYY-MM period by whole months. */
function shiftPeriod(period: string, byMonths: number): string {
  const { y, m } = periodParts(period);
  const d = new Date(y, m - 1 + byMonths, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function currentPeriod(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * The periods a new claim may cover, newest first.
 *
 * The form's selector is built from this and the server validates against the
 * same rule, so the two cannot drift into disagreeing about what is claimable.
 */
export function claimablePeriods(now: Date = new Date()): string[] {
  const current = currentPeriod(now);
  return Array.from({ length: CLAIMABLE_MONTHS }, (_, i) => shiftPeriod(current, -i));
}

/** The oldest period still open. Named in the rejection message. */
export function earliestClaimablePeriod(now: Date = new Date()): string {
  return shiftPeriod(currentPeriod(now), -(CLAIMABLE_MONTHS - 1));
}

/** Period must be YYYY-MM, not in the future, and inside the claimable window. */
export function validatePeriod(period: string, now: Date = new Date()): string | null {
  if (!/^\d{4}-\d{2}$/.test(period)) return 'Period must be in YYYY-MM format.';
  const { y, m } = periodParts(period);
  if (m < 1 || m > 12) return 'Period month must be between 01 and 12.';
  const start = new Date(y, m - 1, 1);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  if (start > thisMonth) return 'A claim period cannot be in the future.';
  if (monthsBehind(period, now) > CLAIMABLE_MONTHS - 1) {
    return `Receipts must be claimed within ${CLAIMABLE_MONTHS} months. ${periodLabel(period)} is no longer open — the earliest period you can still claim is ${periodLabel(earliestClaimablePeriod(now))}.`;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Password rules                                                      */
/* ------------------------------------------------------------------ */

/**
 * Shared so the browser and the server cannot disagree about what is
 * acceptable. The client checks this to avoid a pointless round trip; the
 * server checks it because the client is not where rules are enforced.
 *
 * Length is the requirement that matters, so the floor is high and there are no
 * composition rules — those push people toward `Password1!` and measurably
 * reduce entropy.
 */
export const MIN_PASSWORD_LENGTH = 12;
export const MAX_PASSWORD_LENGTH = 256;

/** Substrings common enough to be tried first in any real attack. */
export const TRIVIAL_PASSWORD_FRAGMENTS = [
  'password',
  '12345678',
  'qwerty',
  'letmein',
  'gdec',
  'expense',
] as const;

/** Returns a message to show the user, or null when the password is acceptable. */
export function checkPassword(password: string): string | null {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Your password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (password.length > MAX_PASSWORD_LENGTH) {
    // Unbounded input into a memory-hard KDF is a denial-of-service vector.
    return `Your password must be at most ${MAX_PASSWORD_LENGTH} characters.`;
  }
  const lower = password.toLowerCase();
  if (TRIVIAL_PASSWORD_FRAGMENTS.some((f) => lower.includes(f))) {
    return 'That password is too easy to guess. Choose something else.';
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Row anomalies                                                       */
/* ------------------------------------------------------------------ */

/**
 * Things wrong with a receipt row.
 *
 * A receipt with a problem is **kept, flagged, and shown to the requester** —
 * not silently dropped. Dropping it loses the evidence and leaves someone
 * wondering why a receipt they uploaded vanished. But nothing carrying an
 * unresolved anomaly may be submitted, because Finance should never be the
 * first party to notice.
 *
 * Shared so the browser and the server agree on what "unresolved" means. The
 * client uses it to block the submit button and explain why; the server uses it
 * to refuse the submission outright, because the client is not where rules are
 * enforced.
 */
export type AnomalyKind =
  | 'missing_date'
  | 'missing_amount'
  | 'missing_category'
  | 'out_of_period'
  | 'duplicate_in_claim'
  | 'duplicate_elsewhere'
  | 'foreign_currency'
  | 'missing_fx_rate'
  | 'vat_mismatch'
  | 'unreadable';

export interface RowAnomaly {
  kind: AnomalyKind;
  /** Shown to the requester verbatim; say what is wrong and what to do. */
  message: string;
}

export interface AnomalyRow {
  id: string;
  date: string;
  receipt: string;
  description: string;
  /** A string while typing in the browser, a number once parsed. Home currency. */
  amount: string | number;
  /** ISO 4217 from the receipt. Absent or 'PHP' means no conversion needed. */
  currency?: string;
  originalAmount?: string | number | null;
  /** The rate used to convert `originalAmount` into `amount`, if one applied. */
  fxRate?: number | null;
  /**
   * The VAT-exclusive subtotal and the VAT, as printed on the receipt — not
   * computed from the total. Absent on a non-VAT receipt, or when the reader
   * could not make them out. See `reconcileVat`.
   */
  printedNet?: number | null;
  printedVat?: number | null;
}

/** Centavo rounding: a printed breakdown may be a hair off its own total. */
const VAT_TOLERANCE = 0.02;

/**
 * Check a receipt's total against the VAT breakdown printed beside it.
 *
 * A Philippine VAT receipt prints all three figures — net of VAT, the VAT, and
 * the VAT-inclusive total — so the total is checkable without trusting any one
 * reading of it. That matters because the total is the figure most often
 * handwritten, and therefore the one most often misread: the same ₱1,000 fuel
 * receipt was read as 1,101, 10,000, 1,000 and 609.74 on four consecutive
 * passes, while the printed 892.86 + 107.14 came back right every time. A 10x
 * overstatement is not a rounding error and Finance should not be the first to
 * notice it.
 *
 * Returns what the printed figures say the total should be, or null when they
 * agree with it or are not both available. It does not correct anything: the
 * requester has the receipt in front of them and decides which figure is real.
 */
export function reconcileVat(row: {
  amount: string | number;
  printedNet?: number | null;
  printedVat?: number | null;
}): { expected: number; claimed: number } | null {
  const net = Number(row.printedNet);
  const vat = Number(row.printedVat);
  // Both must be present and positive. A zero VAT is the ordinary non-VAT
  // receipt, which has nothing to reconcile against.
  if (!Number.isFinite(net) || !Number.isFinite(vat) || net <= 0 || vat <= 0) return null;

  const claimed = typeof row.amount === 'string' ? Number(row.amount) : row.amount;
  if (!Number.isFinite(claimed) || claimed <= 0) return null;

  const expected = Math.round((net + vat) * 100) / 100;
  if (Math.abs(expected - claimed) <= VAT_TOLERANCE) return null;
  return { expected, claimed };
}

/**
 * Every problem with one row, in the order a person would want to fix them.
 *
 * `duplicate_elsewhere` cannot be detected here — it needs the whole claim
 * corpus, which the browser must never hold — so the caller passes it in after
 * asking the server.
 */
export function detectRowAnomalies(
  row: AnomalyRow,
  period: string,
  allRows: readonly AnomalyRow[],
  knownDuplicateWhere?: string | null,
): RowAnomaly[] {
  const found: RowAnomaly[] = [];

  if (!row.date) {
    found.push({ kind: 'missing_date', message: 'This row has no date.' });
  } else {
    const check = validateReceiptDate({ date: row.date, date_ambiguous: false }, period);
    if (check.status === 'reject') {
      found.push({ kind: 'out_of_period', message: check.reason });
    }
  }

  const amount = typeof row.amount === 'string' ? Number(row.amount) : row.amount;
  const printedOriginal =
    typeof row.originalAmount === 'string' ? Number(row.originalAmount) : row.originalAmount;
  // A foreign receipt whose figure was read but not yet converted is not a row
  // missing an amount — it has one, in the wrong currency. Saying "this row
  // needs an amount" sends someone hunting for a number that is already on the
  // receipt in front of them; the currency finding below says the real thing.
  const awaitingConversion =
    (row.currency ?? HOME_CURRENCY).toUpperCase() !== HOME_CURRENCY &&
    !!printedOriginal &&
    Number.isFinite(printedOriginal) &&
    printedOriginal > 0;

  if ((!Number.isFinite(amount) || amount <= 0) && !awaitingConversion) {
    found.push({ kind: 'missing_amount', message: 'This row needs an amount greater than zero.' });
  }

  // Checked against the figure actually on the row, not the one first read, so
  // correcting the amount clears this without anyone having to re-upload.
  const vat = reconcileVat(row);
  if (vat) {
    found.push({
      kind: 'vat_mismatch',
      message:
        `This receipt prints ${row.printedNet!.toFixed(2)} net + ${row.printedVat!.toFixed(2)} VAT, ` +
        `which comes to ${vat.expected.toFixed(2)} — not the ${vat.claimed.toFixed(2)} on this row. ` +
        `Check the total against the receipt and correct whichever figure is wrong.`,
    });
  }

  if (!String(row.description ?? '').trim()) {
    found.push({ kind: 'missing_category', message: 'Choose an expense category for this row.' });
  }

  const dup = findDuplicateInDraft(
    allRows.map((r) => ({ id: r.id, receipt: r.receipt })),
    row.receipt,
    row.id,
  );
  if (dup) {
    found.push({
      kind: 'duplicate_in_claim',
      message: `Receipt No. "${row.receipt}" is already used in ${dup.where}.`,
    });
  }

  // A foreign receipt is not an error — it is a figure the system must not
  // guess. No exchange rate is invented: the requester enters what their card
  // was actually charged, and the printed original stays on the record.
  const currency = (row.currency ?? HOME_CURRENCY).toUpperCase();
  if (currency && currency !== HOME_CURRENCY) {
    const original =
      typeof row.originalAmount === 'string' ? Number(row.originalAmount) : row.originalAmount;
    const shown =
      original && Number.isFinite(original) ? ` (${currency} ${original.toFixed(2)})` : '';

    if (!row.fxRate || !Number.isFinite(row.fxRate)) {
      // No rate on file. The system will not pick one — a rate invented here
      // would decide someone's reimbursement with nobody accountable for it.
      found.push({
        kind: 'missing_fx_rate',
        message: `This receipt is in ${currency}${shown}, and no ${currency} exchange rate has been set. Ask Finance to add one, then re-check — the claim will convert automatically.`,
      });
    } else if (!Number.isFinite(amount) || amount <= 0) {
      found.push({
        kind: 'foreign_currency',
        message: `This receipt is in ${currency}${shown} but has no converted amount. Re-check to apply the current rate.`,
      });
    }
  }

  if (knownDuplicateWhere) {
    found.push({
      kind: 'duplicate_elsewhere',
      message: `Receipt No. "${row.receipt}" was already used in ${knownDuplicateWhere}.`,
    });
  }

  return found;
}

/** True when nothing on this claim would surprise Finance. */
export function claimIsClean(
  rows: readonly AnomalyRow[],
  period: string,
  duplicatesElsewhere: Readonly<Record<string, string | null>> = {},
): boolean {
  return rows.every(
    (r) => detectRowAnomalies(r, period, rows, duplicatesElsewhere[r.id] ?? null).length === 0,
  );
}
